import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import api from '../lib/api';
import { 
  Landmark, Plus, ArrowUpRight, ArrowDownLeft, ArrowLeftRight,
  Calendar, Clock, CheckCircle2, XCircle, AlertTriangle,
  TrendingUp, TrendingDown, Wallet, Send, Bot, RefreshCw,
  Building2, CreditCard, Euro, PiggyBank, FileText, Link2,
  Unlink, Sparkles, Upload, Search, Filter
} from 'lucide-react';

export default function FinancePage() {
  const [activeTab, setActiveTab] = useState('tableau-bord');
  const [loading, setLoading] = useState(true);
  const [tableauBord, setTableauBord] = useState(null);
  const [comptes, setComptes] = useState([]);
  const [virements, setVirements] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  
  // Rapprochement
  const [operations, setOperations] = useState([]);
  const [statutRapprochement, setStatutRapprochement] = useState(null);
  const [analyseIA, setAnalyseIA] = useState(null);
  const [rapprochementLoading, setRapprochementLoading] = useState(false);
  
  // Modals
  const [showNewCompte, setShowNewCompte] = useState(false);
  const [showNewVirement, setShowNewVirement] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showAddOperation, setShowAddOperation] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [showRapprochementManuel, setShowRapprochementManuel] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  
  // Formulaires
  const [newCompte, setNewCompte] = useState({
    nom: '', banque: '', iban: '', bic: '', type_compte: 'courant',
    solde_initial: 0, est_principal: false, notes: ''
  });
  const [newVirement, setNewVirement] = useState({
    compte_source_id: '', compte_destination_id: '', montant: 0, motif: '',
    date_prevue: '', type_virement: 'sortant', beneficiaire_externe: '',
    iban_externe: '', categorie: '', notes: ''
  });
  const [newOperation, setNewOperation] = useState({
    compte_id: '', date_operation: '', libelle: '', montant: 0, reference: '', categorie: ''
  });
  const [csvData, setCsvData] = useState('');
  const [csvCompteId, setCsvCompteId] = useState('');
  
  // Chat IA
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  // Chargement des données
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tbRes, comptesRes, virementsRes, suggestionsRes] = await Promise.all([
        api.get('/finance/tableau-bord').catch(() => ({ data: null })),
        api.get('/finance/comptes-bancaires').catch(() => ({ data: { comptes: [] } })),
        api.get('/finance/virements').catch(() => ({ data: { virements: [] } })),
        api.get('/finance/agent/suggestions').catch(() => ({ data: { suggestions: [] } }))
      ]);
      
      setTableauBord(tbRes.data);
      setComptes(comptesRes.data?.comptes || []);
      setVirements(virementsRes.data?.virements || []);
      setSuggestions(suggestionsRes.data?.suggestions || []);
    } catch (error) {
      console.error('Erreur chargement données finance:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRapprochementData = useCallback(async () => {
    try {
      const [opsRes, statutRes] = await Promise.all([
        api.get('/finance/releves/operations').catch(() => ({ data: { operations: [] } })),
        api.get('/finance/rapprochement/statut').catch(() => ({ data: null }))
      ]);
      
      setOperations(opsRes.data?.operations || []);
      setStatutRapprochement(statutRes.data);
    } catch (error) {
      console.error('Erreur chargement rapprochement:', error);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'rapprochement') {
      loadRapprochementData();
    }
  }, [activeTab, loadRapprochementData]);

  // Créer un compte bancaire
  const handleCreateCompte = async () => {
    try {
      await api.post('/finance/comptes-bancaires', newCompte);
      setShowNewCompte(false);
      setNewCompte({ nom: '', banque: '', iban: '', bic: '', type_compte: 'courant', solde_initial: 0, est_principal: false, notes: '' });
      loadData();
    } catch (error) {
      console.error('Erreur création compte:', error);
    }
  };

  // Créer un virement
  const handleCreateVirement = async () => {
    try {
      await api.post('/finance/virements', newVirement);
      setShowNewVirement(false);
      setNewVirement({ compte_source_id: '', compte_destination_id: '', montant: 0, motif: '', date_prevue: '', type_virement: 'sortant', beneficiaire_externe: '', iban_externe: '', categorie: '', notes: '' });
      loadData();
    } catch (error) {
      console.error('Erreur création virement:', error);
    }
  };

  // Exécuter un virement
  const handleExecuterVirement = async (virementId) => {
    try {
      await api.post(`/finance/virements/${virementId}/executer`);
      loadData();
    } catch (error) {
      console.error('Erreur exécution virement:', error);
    }
  };

  // Ajouter une opération bancaire
  const handleAddOperation = async () => {
    try {
      await api.post('/finance/releves/operation', newOperation);
      setShowAddOperation(false);
      setNewOperation({ compte_id: '', date_operation: '', libelle: '', montant: 0, reference: '', categorie: '' });
      loadRapprochementData();
    } catch (error) {
      console.error('Erreur ajout opération:', error);
    }
  };

  // Import CSV
  const handleImportCSV = async () => {
    try {
      const lines = csvData.trim().split('\n');
      const operations = [];
      
      for (let i = 1; i < lines.length; i++) { // Skip header
        const cols = lines[i].split(';');
        if (cols.length >= 3) {
          operations.push({
            compte_id: csvCompteId,
            date_operation: cols[0]?.trim(),
            libelle: cols[1]?.trim(),
            montant: parseFloat(cols[2]?.trim().replace(',', '.')) || 0,
            reference: cols[3]?.trim() || ''
          });
        }
      }
      
      if (operations.length > 0) {
        await api.post('/finance/releves/import', {
          compte_id: csvCompteId,
          operations
        });
        setShowImportCSV(false);
        setCsvData('');
        setCsvCompteId('');
        loadRapprochementData();
      }
    } catch (error) {
      console.error('Erreur import CSV:', error);
    }
  };

  // Rapprochement automatique
  const handleRapprochementAuto = async () => {
    setRapprochementLoading(true);
    try {
      const result = await api.post('/finance/rapprochement/auto');
      loadRapprochementData();
      loadData();
      
      // Afficher le résultat
      if (result.data) {
        alert(`Rapprochement terminé !\n- ${result.data.nb_rapprochements} correspondance(s) trouvée(s)\n- ${result.data.nb_anomalies} anomalie(s) détectée(s)`);
      }
    } catch (error) {
      console.error('Erreur rapprochement auto:', error);
    } finally {
      setRapprochementLoading(false);
    }
  };

  // Rapprochement manuel
  const handleRapprochementManuel = async (virementId) => {
    if (!selectedOperation) return;
    try {
      await api.post('/finance/rapprochement/manuel', {
        operation_id: selectedOperation.id,
        virement_id: virementId
      });
      setShowRapprochementManuel(false);
      setSelectedOperation(null);
      loadRapprochementData();
      loadData();
    } catch (error) {
      console.error('Erreur rapprochement manuel:', error);
    }
  };

  // Analyse IA
  const handleAnalyseIA = async () => {
    setRapprochementLoading(true);
    try {
      const result = await api.post('/finance/rapprochement/analyse-ia');
      setAnalyseIA(result.data);
    } catch (error) {
      console.error('Erreur analyse IA:', error);
    } finally {
      setRapprochementLoading(false);
    }
  };

  // Chat avec l'agent IA
  const handleSendChat = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput;
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setChatLoading(true);
    
    try {
      const response = await api.post('/finance/agent/chat', {
        message: userMessage,
        conversation_id: conversationId
      });
      
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      if (response.data.conversation_id) {
        setConversationId(response.data.conversation_id);
      }
    } catch (error) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Erreur de connexion avec l\'agent financier.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant || 0);
  };

  const getStatutBadge = (statut) => {
    const config = {
      prevu: { label: 'Prévu', variant: 'outline', icon: Clock },
      valide: { label: 'Validé', variant: 'secondary', icon: CheckCircle2 },
      execute: { label: 'Exécuté', variant: 'default', icon: CheckCircle2 },
      annule: { label: 'Annulé', variant: 'destructive', icon: XCircle }
    };
    const c = config[statut] || config.prevu;
    const Icon = c.icon;
    return (
      <Badge variant={c.variant} className="gap-1">
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  const getTypeIcon = (type) => {
    if (type === 'entrant') return <ArrowDownLeft className="w-4 h-4 text-green-600" />;
    if (type === 'sortant') return <ArrowUpRight className="w-4 h-4 text-red-600" />;
    return <ArrowLeftRight className="w-4 h-4 text-blue-600" />;
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl" data-testid="finance-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Landmark className="w-7 h-7 text-green-700" />
            Banque, Finance & Trésorerie
          </h1>
          <p className="text-gray-600 text-sm mt-1">Suivi financier de votre exploitation</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowChat(true)}>
            <Bot className="w-4 h-4 mr-1" />
            Agent IA
          </Button>
        </div>
      </div>

      {/* Alertes et suggestions */}
      {suggestions.length > 0 && (
        <div className="mb-4 space-y-2">
          {suggestions.map((s, i) => (
            <div 
              key={i}
              className={`p-3 rounded-lg border flex items-center gap-3 ${
                s.type === 'alert' ? 'bg-red-50 border-red-200' :
                s.type === 'warning' ? 'bg-amber-50 border-amber-200' :
                'bg-blue-50 border-blue-200'
              }`}
            >
              <AlertTriangle className={`w-5 h-5 ${
                s.type === 'alert' ? 'text-red-600' :
                s.type === 'warning' ? 'text-amber-600' :
                'text-blue-600'
              }`} />
              <div className="flex-1">
                <p className="font-medium text-sm">{s.titre}</p>
                <p className="text-xs text-gray-600">{s.message}</p>
              </div>
              {s.action && (
                <Button size="sm" variant="outline">{s.action}</Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="tableau-bord">Tableau de bord</TabsTrigger>
          <TabsTrigger value="comptes">Comptes bancaires</TabsTrigger>
          <TabsTrigger value="virements">Virements</TabsTrigger>
          <TabsTrigger value="rapprochement" className="gap-1">
            <Link2 className="w-4 h-4" />
            Rapprochement
          </TabsTrigger>
        </TabsList>

        {/* TABLEAU DE BORD */}
        <TabsContent value="tableau-bord">
          {tableauBord && (
            <div className="space-y-4">
              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Solde total</p>
                        <p className={`text-2xl font-bold ${tableauBord.solde_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatMontant(tableauBord.solde_total)}
                        </p>
                      </div>
                      <Wallet className="w-10 h-10 text-gray-300" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Prévision 30j</p>
                        <p className={`text-2xl font-bold ${tableauBord.solde_prevu_30j >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatMontant(tableauBord.solde_prevu_30j)}
                        </p>
                      </div>
                      <TrendingUp className="w-10 h-10 text-gray-300" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Entrées prévues</p>
                        <p className="text-2xl font-bold text-green-600">
                          +{formatMontant(tableauBord.entrees_prevues_30j)}
                        </p>
                      </div>
                      <ArrowDownLeft className="w-10 h-10 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Sorties prévues</p>
                        <p className="text-2xl font-bold text-red-600">
                          -{formatMontant(tableauBord.sorties_prevues_30j)}
                        </p>
                      </div>
                      <ArrowUpRight className="w-10 h-10 text-red-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Comptes et virements urgents */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Mes comptes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {tableauBord.comptes?.length > 0 ? (
                      <div className="space-y-2">
                        {tableauBord.comptes.map(compte => (
                          <div key={compte.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="font-medium text-sm">{compte.nom}</p>
                                <p className="text-xs text-gray-500">{compte.banque}</p>
                              </div>
                              {compte.est_principal && (
                                <Badge variant="secondary" className="text-xs">Principal</Badge>
                              )}
                            </div>
                            <p className={`font-bold ${compte.solde >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatMontant(compte.solde)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4">Aucun compte configuré</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Virements cette semaine
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {tableauBord.virements_urgents?.length > 0 ? (
                      <div className="space-y-2">
                        {tableauBord.virements_urgents.map(v => (
                          <div key={v.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              {getTypeIcon(v.type_virement)}
                              <div>
                                <p className="font-medium text-sm">{v.motif}</p>
                                <p className="text-xs text-gray-500">{v.date_prevue}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${v.type_virement === 'entrant' ? 'text-green-600' : 'text-red-600'}`}>
                                {v.type_virement === 'entrant' ? '+' : '-'}{formatMontant(v.montant)}
                              </p>
                              {getStatutBadge(v.statut)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4">Aucun virement urgent</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Stats du mois */}
              {tableauBord.stats_mois && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Bilan du mois</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-sm text-gray-500">Entrées</p>
                        <p className="text-xl font-bold text-green-600">+{formatMontant(tableauBord.stats_mois.entrees)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Sorties</p>
                        <p className="text-xl font-bold text-red-600">-{formatMontant(tableauBord.stats_mois.sorties)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Solde net</p>
                        <p className={`text-xl font-bold ${tableauBord.stats_mois.solde_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatMontant(tableauBord.stats_mois.solde_net)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* COMPTES BANCAIRES */}
        <TabsContent value="comptes">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Comptes bancaires</h2>
            <Dialog open={showNewCompte} onOpenChange={setShowNewCompte}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter un compte
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouveau compte bancaire</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Nom du compte</Label>
                      <Input 
                        value={newCompte.nom} 
                        onChange={e => setNewCompte({...newCompte, nom: e.target.value})}
                        placeholder="Compte principal"
                      />
                    </div>
                    <div>
                      <Label>Banque</Label>
                      <Input 
                        value={newCompte.banque} 
                        onChange={e => setNewCompte({...newCompte, banque: e.target.value})}
                        placeholder="Crédit Agricole"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>IBAN</Label>
                      <Input 
                        value={newCompte.iban} 
                        onChange={e => setNewCompte({...newCompte, iban: e.target.value})}
                        placeholder="FR76..."
                      />
                    </div>
                    <div>
                      <Label>BIC</Label>
                      <Input 
                        value={newCompte.bic} 
                        onChange={e => setNewCompte({...newCompte, bic: e.target.value})}
                        placeholder="AGRIFRPP"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Type de compte</Label>
                      <Select value={newCompte.type_compte} onValueChange={v => setNewCompte({...newCompte, type_compte: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="courant">Courant</SelectItem>
                          <SelectItem value="epargne">Épargne</SelectItem>
                          <SelectItem value="professionnel">Professionnel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Solde initial (€)</Label>
                      <Input 
                        type="number"
                        value={newCompte.solde_initial} 
                        onChange={e => setNewCompte({...newCompte, solde_initial: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewCompte(false)}>Annuler</Button>
                  <Button onClick={handleCreateCompte}>Créer le compte</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {comptes.map(compte => (
              <Card key={compte.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      {compte.nom}
                    </CardTitle>
                    {compte.est_principal && <Badge>Principal</Badge>}
                  </div>
                  <CardDescription>{compte.banque}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Solde actuel</span>
                      <span className={`text-xl font-bold ${(compte.solde_calcule || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatMontant(compte.solde_calcule)}
                      </span>
                    </div>
                    {compte.iban && (
                      <p className="text-xs text-gray-400 font-mono">{compte.iban}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {comptes.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                <PiggyBank className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Aucun compte bancaire configuré</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* VIREMENTS */}
        <TabsContent value="virements">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Virements</h2>
            <Dialog open={showNewVirement} onOpenChange={setShowNewVirement}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Nouveau virement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Planifier un virement</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Type de virement</Label>
                    <Select value={newVirement.type_virement} onValueChange={v => setNewVirement({...newVirement, type_virement: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sortant">Sortant (paiement)</SelectItem>
                        <SelectItem value="entrant">Entrant (encaissement)</SelectItem>
                        <SelectItem value="interne">Interne (entre mes comptes)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {newVirement.type_virement !== 'entrant' && comptes.length > 0 && (
                    <div>
                      <Label>Compte source</Label>
                      <Select value={newVirement.compte_source_id} onValueChange={v => setNewVirement({...newVirement, compte_source_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                        <SelectContent>
                          {comptes.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.nom} ({c.banque})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {newVirement.type_virement === 'sortant' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Bénéficiaire</Label>
                        <Input 
                          value={newVirement.beneficiaire_externe} 
                          onChange={e => setNewVirement({...newVirement, beneficiaire_externe: e.target.value})}
                          placeholder="Nom du bénéficiaire"
                        />
                      </div>
                      <div>
                        <Label>IBAN bénéficiaire</Label>
                        <Input 
                          value={newVirement.iban_externe} 
                          onChange={e => setNewVirement({...newVirement, iban_externe: e.target.value})}
                          placeholder="FR76..."
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Montant (€)</Label>
                      <Input 
                        type="number"
                        value={newVirement.montant} 
                        onChange={e => setNewVirement({...newVirement, montant: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <Label>Date prévue</Label>
                      <Input 
                        type="date"
                        value={newVirement.date_prevue} 
                        onChange={e => setNewVirement({...newVirement, date_prevue: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Motif</Label>
                    <Input 
                      value={newVirement.motif} 
                      onChange={e => setNewVirement({...newVirement, motif: e.target.value})}
                      placeholder="Paiement facture fournisseur..."
                    />
                  </div>
                  
                  <div>
                    <Label>Catégorie</Label>
                    <Select value={newVirement.categorie} onValueChange={v => setNewVirement({...newVirement, categorie: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fournisseurs">Fournisseurs</SelectItem>
                        <SelectItem value="salaires">Salaires</SelectItem>
                        <SelectItem value="charges">Charges fixes</SelectItem>
                        <SelectItem value="investissements">Investissements</SelectItem>
                        <SelectItem value="subventions">Subventions</SelectItem>
                        <SelectItem value="ventes">Ventes</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewVirement(false)}>Annuler</Button>
                  <Button onClick={handleCreateVirement}>Créer le virement</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-2">
            {virements.map(v => (
              <Card key={v.id} className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getTypeIcon(v.type_virement)}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{v.motif}</p>
                        {v.rapproche && (
                          <Badge variant="outline" className="gap-1 text-green-600 border-green-300">
                            <Link2 className="w-3 h-3" /> Pointé
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {v.date_prevue} • {v.reference}
                        {v.beneficiaire_externe && ` • ${v.beneficiaire_externe}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`font-bold ${v.type_virement === 'entrant' ? 'text-green-600' : 'text-red-600'}`}>
                        {v.type_virement === 'entrant' ? '+' : '-'}{formatMontant(v.montant)}
                      </p>
                      {getStatutBadge(v.statut)}
                    </div>
                    {v.statut === 'prevu' && (
                      <Button size="sm" variant="outline" onClick={() => handleExecuterVirement(v.id)}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Exécuter
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            {virements.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Send className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Aucun virement planifié</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* RAPPROCHEMENT BANCAIRE */}
        <TabsContent value="rapprochement">
          <div className="space-y-4">
            {/* Statut global */}
            {statutRapprochement && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Link2 className="w-5 h-5" />
                    Statut du rapprochement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Taux de rapprochement</span>
                      <span className={`text-lg font-bold ${statutRapprochement.taux_rapprochement >= 80 ? 'text-green-600' : statutRapprochement.taux_rapprochement >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {statutRapprochement.taux_rapprochement}%
                      </span>
                    </div>
                    <Progress value={statutRapprochement.taux_rapprochement} className="h-2" />
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Opérations bancaires</p>
                        <p className="text-lg font-semibold">{statutRapprochement.operations.rapprochees} / {statutRapprochement.operations.total}</p>
                        {statutRapprochement.operations.non_rapprochees > 0 && (
                          <p className="text-xs text-amber-600">{statutRapprochement.operations.non_rapprochees} non pointée(s)</p>
                        )}
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Virements exécutés</p>
                        <p className="text-lg font-semibold">{statutRapprochement.virements.rapproches} / {statutRapprochement.virements.total}</p>
                        {statutRapprochement.virements.non_rapproches > 0 && (
                          <p className="text-xs text-amber-600">{statutRapprochement.virements.non_rapproches} non pointé(s)</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleRapprochementAuto} disabled={rapprochementLoading}>
                <Sparkles className="w-4 h-4 mr-1" />
                Rapprochement automatique
              </Button>
              <Button variant="outline" onClick={handleAnalyseIA} disabled={rapprochementLoading}>
                <Bot className="w-4 h-4 mr-1" />
                Analyse IA
              </Button>
              <Dialog open={showAddOperation} onOpenChange={setShowAddOperation}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter une opération
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nouvelle opération bancaire</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Compte</Label>
                      <Select value={newOperation.compte_id} onValueChange={v => setNewOperation({...newOperation, compte_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                        <SelectContent>
                          {comptes.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Date</Label>
                        <Input 
                          type="date"
                          value={newOperation.date_operation}
                          onChange={e => setNewOperation({...newOperation, date_operation: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Montant (€)</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={newOperation.montant}
                          onChange={e => setNewOperation({...newOperation, montant: parseFloat(e.target.value) || 0})}
                          placeholder="Positif = crédit, Négatif = débit"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Libellé</Label>
                      <Input 
                        value={newOperation.libelle}
                        onChange={e => setNewOperation({...newOperation, libelle: e.target.value})}
                        placeholder="Description de l'opération"
                      />
                    </div>
                    <div>
                      <Label>Référence (optionnel)</Label>
                      <Input 
                        value={newOperation.reference}
                        onChange={e => setNewOperation({...newOperation, reference: e.target.value})}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddOperation(false)}>Annuler</Button>
                    <Button onClick={handleAddOperation}>Ajouter</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              <Dialog open={showImportCSV} onOpenChange={setShowImportCSV}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Upload className="w-4 h-4 mr-1" />
                    Importer relevé CSV
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Import relevé bancaire CSV</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Compte bancaire</Label>
                      <Select value={csvCompteId} onValueChange={setCsvCompteId}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner le compte..." /></SelectTrigger>
                        <SelectContent>
                          {comptes.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.nom} ({c.banque})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Format attendu (séparateur: point-virgule)</Label>
                      <p className="text-xs text-gray-500 mb-2">Date;Libellé;Montant;Référence</p>
                      <Textarea 
                        value={csvData}
                        onChange={e => setCsvData(e.target.value)}
                        placeholder="Date;Libellé;Montant;Référence
2026-02-10;Virement reçu DUPONT;1500,00;VIR123
2026-02-12;Prélèvement EDF;-85,50;PREL456"
                        rows={8}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowImportCSV(false)}>Annuler</Button>
                    <Button onClick={handleImportCSV} disabled={!csvCompteId || !csvData}>Importer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Analyse IA */}
            {analyseIA && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                    <Bot className="w-5 h-5" />
                    Analyse IA du rapprochement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="whitespace-pre-wrap text-sm">{analyseIA.analyse}</div>
                  <Button variant="ghost" size="sm" className="mt-2" onClick={() => setAnalyseIA(null)}>
                    Fermer
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Liste des opérations non rapprochées */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Opérations bancaires
                </CardTitle>
                <CardDescription>
                  {operations.filter(o => !o.rapproche).length} opération(s) à pointer
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {operations.map(op => (
                    <div 
                      key={op.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        op.rapproche ? 'bg-green-50 border-green-200' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {op.montant > 0 ? (
                          <ArrowDownLeft className="w-5 h-5 text-green-600" />
                        ) : (
                          <ArrowUpRight className="w-5 h-5 text-red-600" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{op.libelle}</p>
                          <p className="text-xs text-gray-500">{op.date_operation} {op.reference && `• ${op.reference}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className={`font-bold ${op.montant > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatMontant(op.montant)}
                        </p>
                        {op.rapproche ? (
                          <Badge variant="outline" className="text-green-600 border-green-300 gap-1">
                            <Link2 className="w-3 h-3" /> Pointé
                          </Badge>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedOperation(op);
                              setShowRapprochementManuel(true);
                            }}
                          >
                            <Link2 className="w-3 h-3 mr-1" /> Pointer
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {operations.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>Aucune opération bancaire</p>
                      <p className="text-sm">Importez votre relevé ou ajoutez des opérations manuellement</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal Rapprochement Manuel */}
      <Dialog open={showRapprochementManuel} onOpenChange={setShowRapprochementManuel}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Rapprochement manuel</DialogTitle>
          </DialogHeader>
          {selectedOperation && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-500">Opération sélectionnée</p>
                <p className="font-medium">{selectedOperation.libelle}</p>
                <p className="text-sm">{selectedOperation.date_operation} • {formatMontant(selectedOperation.montant)}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-2">Sélectionner le virement correspondant :</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {virements
                    .filter(v => v.statut === 'execute' && !v.rapproche)
                    .filter(v => Math.abs(Math.abs(v.montant) - Math.abs(selectedOperation.montant)) < 1)
                    .map(v => (
                      <div 
                        key={v.id}
                        className="p-2 border rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleRapprochementManuel(v.id)}
                      >
                        <p className="font-medium text-sm">{v.motif}</p>
                        <p className="text-xs text-gray-500">{v.date_execution || v.date_prevue} • {formatMontant(v.montant)}</p>
                      </div>
                    ))
                  }
                  {virements.filter(v => v.statut === 'execute' && !v.rapproche).length === 0 && (
                    <p className="text-gray-500 text-sm">Aucun virement exécuté non rapproché</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRapprochementManuel(false)}>Annuler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Chat Agent IA */}
      <Dialog open={showChat} onOpenChange={setShowChat}>
        <DialogContent className="max-w-2xl h-[600px] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-green-600" />
              Agent Financier IA
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="flex-1 p-4 border rounded-lg bg-gray-50">
            {chatMessages.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <Bot className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Bonjour ! Je suis votre assistant financier.</p>
                <p className="text-sm">Posez-moi vos questions sur votre trésorerie.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {chatMessages.map((msg, i) => (
                  <div 
                    key={i} 
                    className={`p-3 rounded-lg ${
                      msg.role === 'user' 
                        ? 'bg-green-100 ml-8' 
                        : 'bg-white border mr-8'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))}
                {chatLoading && (
                  <div className="bg-white border p-3 rounded-lg mr-8">
                    <p className="text-sm text-gray-500 animate-pulse">Réflexion en cours...</p>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          
          <div className="flex gap-2 pt-2">
            <Input 
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendChat()}
              placeholder="Posez votre question..."
              disabled={chatLoading}
            />
            <Button onClick={handleSendChat} disabled={chatLoading || !chatInput.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
